/**
 * Calcula el adeudo visible al usuario, respetando el período de gracia
 * 
 * @param {number} adeudoReal - El adeudo completo incluyendo intereses (valor almacenado en Firestore)
 * @param {number} mesesAdeudo - Número de meses que el residente debe
 * @param {number} mesesPermitidos - Número de meses permitidos sin mostrar intereses (período de gracia)
 * @param {number} cuotaMensual - Valor de la cuota mensual
 * @returns {number} El adeudo que se debe mostrar al usuario
 * 
 * Lógica:
 * - Si mesesAdeudo < mesesPermitidos: Mostrar solo las cuotas (mesesAdeudo × cuotaMensual)
 * - Si mesesAdeudo >= mesesPermitidos: Mostrar el adeudo completo con todos los intereses
 * - Fallback: si hay adeudo pero mesesAdeudo no está informado (< 1), mostrar el adeudo real
 */
export const calcularAdeudoVisible = (
  adeudoReal = 0,
  mesesAdeudo = 0,
  mesesPermitidos = 1,
  cuotaMensual = 0
) => {
  // Validar que los valores sean números válidos
  const adeudo = Number(adeudoReal ?? 0);
  const meses = Number(mesesAdeudo ?? 0);
  const mesesGracia = Number(mesesPermitidos ?? 1);
  const cuota = Number(cuotaMensual ?? 0);

  if (!Number.isFinite(adeudo) || !Number.isFinite(meses) || !Number.isFinite(mesesGracia) || !Number.isFinite(cuota)) {
    console.warn('⚠️ calcularAdeudoVisible: valores inválidos', { adeudo, meses, mesesGracia, cuota });
    return 0;
  }

  // Si no hay adeudo, retornar 0
  if (adeudo <= 0) {
    return 0;
  }

  // Fallback: si no tenemos meses de adeudo pero sí adeudo, mostrar adeudo real
  if (meses < 1) {
    console.log('ℹ️ calcularAdeudoVisible: mesesAdeudo ausente o 0; mostrando adeudo real', { adeudo });
    return adeudo;
  }

  // Si está dentro del período de gracia, mostrar solo cuotas sin intereses
  if (meses <= mesesGracia && cuota > 0) {
    const adeudoMasked = meses * cuota;
    console.log('✨ Período de gracia aplicado:', { 
      meses, 
      mesesGracia, 
      cuota, 
      adeudoReal: adeudo, 
      adeudoVisible: adeudoMasked,
      config: `Gracia: ${mesesGracia} meses`
    });
    return adeudoMasked;
  }

  // Ya superó el período de gracia, mostrar adeudo completo
  console.log('📊 Fuera de gracia:', { 
    meses, 
    mesesGracia, 
    adeudoCompleto: adeudo,
    config: `Gracia: ${mesesGracia} meses - superado`
  });
  return adeudo;
};

/**
 * Versión simplificada que extrae valores de un objeto casa/residente
 * @param {Object} casa - El objeto casa con propiedades adeudo, mesesAdeudo, etc.
 * @param {Object} financialConfig - La configuración financiera con mesesPermitidos y cuotaMensual
 * @returns {number} El adeudo visible
 */
export const calcularAdeudoVisibleDesdeObjeto = (casa = {}, financialConfig = {}) => {
  const adeudoReal = Number(casa?.adeudo ?? casa?.deuda ?? casa?.montoAdeudo ?? 0);
  const adeudoCapital = Number(casa?.adeudoCapital ?? 0);
  const adeudoIntereses = Number(casa?.adeudoIntereses ?? casa?.adeudoInteres ?? 0);
  const mesesAdeudo = Number(casa?.mesesAdeudo ?? casa?.meses_deuda ?? casa?.meses ?? 0);
  
  // CORREGIDO: Usar mesesPermitidosGracia guardado en la casa si existe, sino usar configuración
  // Esto evita que cambios en la configuración afecten a residentes que ya están en período de gracia
  const mesesPermitidosGraciaGuardado = casa?.mesesPermitidosGracia;
  const mesesPermitidosConfig = Number(
    financialConfig?.mesesPermitidos ?? 
    financialConfig?.interesesMesesPermitidosDeuda ?? 
    financialConfig?.meses_permitidos ?? 
    1
  );
  const mesesPermitidos = (mesesPermitidosGraciaGuardado !== undefined && mesesPermitidosGraciaGuardado !== null)
    ? Number(mesesPermitidosGraciaGuardado)  // Usar el valor guardado cuando empezó el período de gracia
    : mesesPermitidosConfig;  // Usar la configuración actual si no hay valor guardado
  
  const cuotaMensual = Number(
    financialConfig?.cuotaMensual ?? 
    financialConfig?.montoMensual ?? 
    financialConfig?.cuota_mensual ?? 
    0
  );
  
  // Obtener día de sanciones y día actual para verificar días de gracia
  const sancionesInicioDia = Number(
    financialConfig?.sancionesInicioDia ?? 
    financialConfig?.corteDia ?? 
    1
  );
  const diaActual = new Date().getDate();

  // Si no hay adeudo, retornar 0
  if (adeudoReal <= 0) {
    return 0;
  }

  // Si no hay meses de adeudo pero sí adeudo, mostrar adeudo real
  if (mesesAdeudo < 1) {
    console.log('ℹ️ calcularAdeudoVisible: mesesAdeudo ausente o 0; mostrando adeudo real', { adeudoReal });
    return adeudoReal;
  }

  // CORREGIDO: Verificar meses Y días de gracia para determinar qué mostrar
  // Durante meses de gracia: mostrar solo adeudoCapital
  // Solo mostrar adeudo real (total) cuando se cumplieron AMBOS: meses Y días de gracia
  // IMPORTANTE: Si mesesAdeudo = mesesPermitidos Y diaActual >= sancionesInicioDia, ya cumplió ambos
  const dentroDeMesesGracia = mesesAdeudo < mesesPermitidos;
  const dentroDeDiasGracia = diaActual < sancionesInicioDia;
  const cumplioMesesYGracia = mesesAdeudo >= mesesPermitidos && diaActual >= sancionesInicioDia;
  
  // Si cumplió AMBOS (meses Y días de gracia), mostrar adeudo total
  if (cumplioMesesYGracia) {
    console.log('📊 Cumplió meses Y días de gracia - mostrando adeudo total:', { 
      mesesAdeudo, 
      mesesPermitidos, 
      diaActual,
      sancionesInicioDia,
      adeudoCapital,
      adeudoReal,
      adeudoIntereses,
      adeudoVisible: adeudoReal,
      config: `Gracia: ${mesesPermitidos} meses, día ${sancionesInicioDia} - cumplido, mostrando total`
    });
    return adeudoReal;
  }
  
  // Si está dentro de meses de gracia (mesesAdeudo < mesesPermitidos), mostrar solo capital
  if (dentroDeMesesGracia) {
    console.log('✨ Período de gracia aplicado (meses):', { 
      mesesAdeudo, 
      mesesPermitidos, 
      diaActual,
      sancionesInicioDia,
      dentroDeMesesGracia,
      dentroDeDiasGracia,
      adeudoCapital,
      adeudoReal,
      adeudoIntereses,
      adeudoVisible: adeudoCapital,
      config: `Gracia: ${mesesPermitidos} meses - mostrando solo capital`
    });
    return adeudoCapital;
  }
  
  // Caso especial: mesesAdeudo = mesesPermitidos pero aún no pasó el día de sanciones
  // Aún mostrar solo capital hasta que pase el día de sanciones
  if (mesesAdeudo === mesesPermitidos && diaActual < sancionesInicioDia) {
    console.log('⏳ Cumplió meses de gracia pero aún en días de gracia:', { 
      mesesAdeudo, 
      mesesPermitidos, 
      diaActual,
      sancionesInicioDia,
      adeudoCapital,
      adeudoReal,
      config: `Gracia: ${mesesPermitidos} meses cumplido, pero día ${sancionesInicioDia} aún no - mostrando solo capital`
    });
    return adeudoCapital;
  }
  
  // Si superó meses de gracia pero aún no pasó el día de sanciones
  // Aún mostrar solo capital hasta que pase el día de sanciones
  console.log('⏳ Superó meses de gracia pero aún en días de gracia:', { 
    mesesAdeudo, 
    mesesPermitidos, 
    diaActual,
    sancionesInicioDia,
    adeudoCapital,
    adeudoReal,
    config: `Gracia: ${mesesPermitidos} meses superado, pero día ${sancionesInicioDia} aún no - mostrando solo capital`
  });
  return adeudoCapital;
};
